// ProfilePost.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withTranslation } from 'react-i18next';

import { USER_PROFILE_HOME_CATEGORIES } from '../RemoteConfigKeys.js';

import Redirect from '../container/Redirect.js';
import UnpublishedPostCard from '../container/UnpublishedPostCard.js';
import LocalUnpublishedPostCard from '../container/LocalUnpublishedPostCard.js';
import SortButton from '../container/SortButton.js';
import ProfileCategories from '../container/ProfileCategories.js';

import FeedComponent from '../component/FeedComponent.jsx';
import Loading from '../component/Loading.jsx';
import {
  InfiniteScroller,
  ScrollItem,
} from '../component/InfiniteScroller.jsx';

import {
  ProfileListType,
  ProfileListPublishStatus,
} from '../resource/profileConstants.js';
import { ButtonId } from '../resource/mixpanel.js';
import objectifyArrayById from '../resource/objectifyArrayById.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import getTranslationKey from '../resource/getTranslationKey.js';
import { QueryType } from '../resource/feedConstants.js';

import { color, textColor } from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import media from '../style/media.js';

import CreatorEmptyPostIconSrouce from '../../img/img_empty_nopost_creator.svg';
import UserEmptyPostIconSrouce from '../../img/img_empty_nopost.svg';
import ShortsEmptyIconSource from '../../img/img_nofollower.svg';

export class ProfilePost extends React.PureComponent {
  state = {
    isServer: true,
  };
  profilePostListElement = React.createRef();
  nextTick = null;
  scrollToElementTimeout = null;
  locationState = {};
  updateProfileFeedUnixTimestamp = () => {
    const {
      isMeProfile,
      profileFeedUnixTimestamp,
      updateProfileFeedUnixTimestamp,
    } = this.props;
    if (isMeProfile && !profileFeedUnixTimestamp) {
      return updateProfileFeedUnixTimestamp({
        unixTimestamp: getCurrentUnixTimestamp(),
      });
    }
  };
  /**
   * Get selected filters except first item, since first item is `ALL`.
   * We need to prevent first and other item been selected at same time since first item is "All".
   * @return {array} - return non `ALL` selected filters.
   */
  getSelectedFiltersExceptFirstItem = () => {
    const { filters, selectedFilters } = this.props;
    return selectedFilters.filter(i => i !== filters[0]);
  };
  getShouldShowLocalUnpublishedPost = () => {
    const { publishStatus, isMeProfile, uploadPostIds } = this.props;
    const { isServer } = this.state; // Prevent re-hydrate dom shift on client side
    const isUnpublished = ProfileListPublishStatus.DRAFT === publishStatus;
    const selectedFilters = this.getSelectedFiltersExceptFirstItem();
    const shouldShowPostDraftList =
      !!uploadPostIds.length &&
      !isServer &&
      isMeProfile &&
      isUnpublished &&
      // Local unpublished only show in `ALL` filter.
      !selectedFilters.length;
    return shouldShowPostDraftList;
  };
  getIsEmpty = () => {
    const { outboxFeedName, isFirstPageFetched, postIds } = this.props;
    const shouldShowPostDraftList = this.getShouldShowLocalUnpublishedPost();
    const isOutboxFeedEmpty =
      !outboxFeedName ||
      (outboxFeedName && isFirstPageFetched && postIds.length === 0);
    const isEmpty = isOutboxFeedEmpty && !shouldShowPostDraftList;
    return isEmpty;
  };
  fetchUnpublishedPosts = ({ page, shouldSkipLocalCache }) => {
    const {
      userId,
      meId,
      feedNameWithQuery,
      publishStatus,
      profileFeedUnixTimestamp,
      isMeProfile,
      fetchFeeds,
    } = this.props;
    const usingUserId = meId && isMeProfile ? meId : userId;
    const isUnpublished = ProfileListPublishStatus.DRAFT === publishStatus;
    if (!usingUserId || !isUnpublished) {
      return;
    }
    return fetchFeeds({
      type: feedNameWithQuery,
      page,
      unixTimestamp: profileFeedUnixTimestamp,
      shouldSkipLocalCache,
    });
  };
  renderLoading = () => {
    return (
      <ItemWrapper>
        <LoadingWrapper>
          <Loading />
        </LoadingWrapper>
      </ItemWrapper>
    );
  };
  renderEmpty = () => {
    const { listType, publishStatus, isMeProfile, t, openUploadMenuModal } =
      this.props;
    const isUnpublished = ProfileListPublishStatus.DRAFT === publishStatus;
    if (ProfileListType.SHORT === listType || isUnpublished) {
      return (
        <EmptyWrapper>
          <EmptyIcon src={ShortsEmptyIconSource} />
          {t('it_is_empty_here', {
            ns: TranslationNamespace.GENERAL,
          })}
        </EmptyWrapper>
      );
    }
    if (!isMeProfile) {
      return (
        <EmptyWrapper>
          <EmptyIcon src={UserEmptyPostIconSrouce} />
          {t('empty_post_list', {
            ns: TranslationNamespace.PROFILE,
          })}
        </EmptyWrapper>
      );
    }
    return (
      <EmptyWrapper>
        <EmptyIcon src={CreatorEmptyPostIconSrouce} />
        <EmptyActionButton
          data-element_id={
            ProfileListType.STORY === listType
              ? ButtonId.Profile.ButtonCreateStory
              : ProfileListType.IMAGE === listType
                ? ButtonId.Profile.ButtonCreateImage
                : ProfileListType.FLIX === listType
                  ? ButtonId.Profile.ButtonCreateVideo
                  : ButtonId.Profile.ButtonCreatePost
          }
          onClick={() => {
            return openUploadMenuModal();
          }}
        >
          {t('create_post_to_earn', {
            ns: TranslationNamespace.PROFILE,
          })}
        </EmptyActionButton>
      </EmptyWrapper>
    );
  };
  getLocationState = () => {
    const { listType, userId } = this.props;
    const key = `${userId}`;
    if (!this.locationState[key]) {
      this.locationState[key] =
        ProfileListType.STORY === listType
          ? {
              listPath: ['outbox', ProfileListType.STORY, userId],
            }
          : {};
    }
    return this.locationState[key];
  };
  renderCategories = () => {
    const { userId } = this.props;
    return (
      <ProfileCategories
        remoteConfigKey={USER_PROFILE_HOME_CATEGORIES}
        userId={userId}
      />
    );
  };
  renderList = () => {
    const {
      feedNameWithQuery,
      profileFeedUnixTimestamp,
      nextPage,
      isNextPageFetching,
      userId,
      postIds,
      uploadPostIds,
      pinnedItemIds,
      listType,
      publishStatus,
    } = this.props;
    const isUnpublished = ProfileListPublishStatus.DRAFT === publishStatus;
    if (isUnpublished) {
      return (
        <InfiniteScroller
          pageStart={1}
          useWindow
          threshold={100}
          hasMore={nextPage != null}
          isFetching={isNextPageFetching}
          loadMore={() => this.fetchUnpublishedPosts({ page: nextPage })}
        >
          <ItemList>
            <PostDraftListWrapper>
              {this.getShouldShowLocalUnpublishedPost() &&
                uploadPostIds.map(uploadJobId => (
                  <LocalUnpublishedPostCard
                    key={uploadJobId}
                    uploadJobId={uploadJobId}
                  />
                ))}
            </PostDraftListWrapper>
            <ScrollItem loader={this.renderLoading()}>
              {postIds.map(id => (
                <UnpublishedPostCard key={id} messageId={id} />
              ))}
            </ScrollItem>
          </ItemList>
        </InfiniteScroller>
      );
    }
    const componentProps = {
      category: feedNameWithQuery,
      categoryIndex: 0,
      feedUnixTimestamp: profileFeedUnixTimestamp,
      pinnedItemIds,
    };
    switch (listType) {
      case ProfileListType.FLIX:
        componentProps.shortVideoOrigin = 'profile';
        componentProps.shouldIncrementHeading = true;
        componentProps.shouldTrackProfilePostEvent = true;
        componentProps.shouldShowAuthor = false;
        break;
      case ProfileListType.STORY:
        componentProps.locationState = {
          listPath: ['outbox', ProfileListType.STORY, userId],
        };
        break;
      case ProfileListType.SHORT:
        componentProps.isProfileCategory = true;
        break;
      case ProfileListType.IMAGE:
      default:
        break;
    }
    return <FeedComponent {...componentProps} />;
  };
  renderFeedConditions = () => {
    const {
      outboxFeedName,
      feedNameRemoteConfigKey,
      filters,
      t,
      setSelectedFeedQueryDraftOption,
      setSelectedFeedQueryOption,
    } = this.props;
    const selectedFilters = this.getSelectedFiltersExceptFirstItem();
    return (
      <ConditionsWrapper>
        <Filters>
          {filters.map((item, index) => {
            const isActive =
              index === 0
                ? selectedFilters.length === 0
                : selectedFilters.includes(item);
            return (
              <FilterButton
                key={item}
                isActive={isActive}
                data-element_id={ButtonId.All.ButtonFilterOption}
                data-tracking_payload={{
                  'filter.conditions': item,
                }}
                onClick={() => {
                  setSelectedFeedQueryDraftOption({
                    queryType: QueryType.FILTER,
                    option: item,
                    key: '',
                    remoteConfigKeyName: feedNameRemoteConfigKey,
                    baseFeedName: outboxFeedName,
                  });
                  setSelectedFeedQueryOption({
                    queryType: QueryType.FILTER,
                    baseFeedName: outboxFeedName,
                    remoteConfigKeyName: feedNameRemoteConfigKey,
                  });
                }}
              >
                {t(getTranslationKey({ key: item }), {
                  ns: TranslationNamespace.FEED,
                })}
              </FilterButton>
            );
          })}
        </Filters>
        {!this.getIsEmpty() && (
          <SortButton
            remoteConfigKeyName={feedNameRemoteConfigKey}
            baseFeedName={outboxFeedName}
            mixpanelButtonId={ButtonId.Profile.ButtonProfileSorting}
            defaultItemI18nKey={'outbox_default_sorting'}
          />
        )}
      </ConditionsWrapper>
    );
  };
  componentDidMount() {
    this.fetchUnpublishedPosts({ page: 1 });
    this.nextTick = setTimeout(() => {
      this.updateProfileFeedUnixTimestamp();
      this.setState({
        isServer: false,
      });
    }, 0);
    const { setMessageHeightInFeed } = this.props;
    this.resizeObserver = new ResizeObserver(entries => {
      const messageObject = objectifyArrayById({
        array: entries.map(entry => ({
          id: entry.target.getAttribute('data-message_id'),
          messageHeight: entry.target.clientHeight,
        })),
      });
      return setMessageHeightInFeed({
        feedName: 'profile',
        messageObject,
      });
    });
  }
  componentDidUpdate(prevProps) {
    const { meId, feedNameWithQuery, profileFeedUnixTimestamp, isMeProfile } =
      this.props;
    const isMeChanged = meId !== prevProps.meId;
    const isFeedNameWithQueryChanged =
      feedNameWithQuery !== prevProps.feedNameWithQuery;
    const isTimestampChanged =
      prevProps.profileFeedUnixTimestamp !== profileFeedUnixTimestamp;
    const isMeProfileChanged = isMeProfile !== prevProps.isMeProfile;
    if (isMeProfileChanged) {
      this.updateProfileFeedUnixTimestamp();
    }
    if (isMeChanged || isFeedNameWithQueryChanged || isTimestampChanged) {
      this.fetchUnpublishedPosts({
        page: 1,
        shouldSkipLocalCache: isTimestampChanged && !isFeedNameWithQueryChanged,
      });
    }
  }
  componentWillUnmount() {
    clearTimeout(this.nextTick);
    clearTimeout(this.scrollToElementTimeout);
    this.resizeObserver?.disconnect();
  }
  render() {
    const { userId, listType, publishStatus, isMeProfile } = this.props;
    const isUnpublished = ProfileListPublishStatus.DRAFT === publishStatus;
    const isPublished = ProfileListPublishStatus.PUBLISHED === publishStatus;
    if (isUnpublished && !isMeProfile) {
      return <Redirect to={`/user/${userId}`} />;
    }
    if (isPublished && ProfileListType.HOME === listType) {
      return (
        <StyledProfileList ref={this.profilePostListElement}>
          {this.renderCategories()}
        </StyledProfileList>
      );
    }
    const isEmpty = this.getIsEmpty();
    return (
      <StyledProfileList ref={this.profilePostListElement}>
        {this.renderFeedConditions()}
        {isEmpty ? this.renderEmpty() : this.renderList()}
      </StyledProfileList>
    );
  }
}

ProfilePost.propTypes = {
  userId: PropTypes.string,
  meId: PropTypes.string,
  feedNameWithQuery: PropTypes.string,
  outboxFeedName: PropTypes.string,
  feedNameRemoteConfigKey: PropTypes.string,
  listType: PropTypes.string,
  publishStatus: PropTypes.string,
  userProfileStoryLayout: PropTypes.string,
  nextPage: PropTypes.number,
  profileFeedUnixTimestamp: PropTypes.number,
  isFirstPageFetched: PropTypes.bool,
  isNextPageFetching: PropTypes.bool,
  isMeProfile: PropTypes.bool,
  pinnedItemIds: PropTypes.array,
  postIds: PropTypes.array,
  uploadPostIds: PropTypes.array,
  filters: PropTypes.array,
  selectedFilters: PropTypes.array,
  t: PropTypes.func.isRequired,
  openUploadMenuModal: PropTypes.func,
  fetchFeeds: PropTypes.func,
  setMessageHeightInFeed: PropTypes.func,
  updateProfileFeedUnixTimestamp: PropTypes.func,
  setSelectedFeedQueryDraftOption: PropTypes.func,
  setSelectedFeedQueryOption: PropTypes.func,
};

ProfilePost.defaultProps = {
  userId: null,
  meId: null,
  feedNameWithQuery: null,
  outboxFeedName: null,
  feedNameRemoteConfigKey: null,
  listType: null,
  publishStatus: null,
  nextPage: null,
  profileFeedUnixTimestamp: null,
  userProfileStoryLayout: null,
  isFirstPageFetched: false,
  isNextPageFetching: false,
  isMeProfile: false,
  pinnedItemIds: [],
  postIds: [],
  uploadPostIds: [],
  filters: [],
  selectedFilters: [],
  openUploadMenuModal: () => null,
  fetchFeeds: () => null,
  setMessageHeightInFeed: () => null,
  updateProfileFeedUnixTimestamp: () => null,
  setSelectedFeedQueryOption: () => null,
};

const StyledProfileList = styled.div`
  background-color: ${color.black};
`;

const ConditionsWrapper = styled.div`
  padding: 20px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  > * {
    :first-child {
      margin-left: 0px;
    }
    margin-left: 8px;
  }
  ${media.mobile`
    padding: 8px 16px;
  `};
  :empty {
    padding: 0px;
    margin-bottom: 20px;
    ${media.mobile`
      margin-bottom: 12px;
    `};
  }
`;

const Filters = styled.div``;

const FilterButton = styled.button.attrs(({ isActive }) => ({
  style: {
    color: isActive ? color.neutral[0] : color.neutral[100],
    borderColor: isActive ? color.sky : color.neutral[100],
    backgroundColor: isActive ? color.sky : 'transparent',
  },
}))`
  ${ResetButtonStyle};
  margin-right: 8px;
  border-width: 1px;
  border-style: solid;
  border-radius: 4px;
  height: 32px;
  padding: 0px 8px;
  display: inline-flex;
  align-items: center;
  font-size: 16px;
  line-height: 1.5;
  ${media.mobile`
    height: 24px;
    font-size: 12px;
  `};
`;

const PostDraftListWrapper = styled.div`
  margin-bottom: 1px;
  width: 100%;
`;

const ItemList = styled.div`
  ${({ isGrid, isThin }) => {
    if (isGrid) {
      return css`
        display: grid;
        grid-template-columns: repeat(
          auto-fill,
          minmax(${isThin ? 168 : 200}px, 1fr)
        );
        place-items: center;
        padding: 16px;
        gap: 20px;
        justify-content: center;
        ${media.mobile`
          gap: 8px;
          grid-template-columns: repeat(
            auto-fill,
            minmax(${isThin ? 112 : 140}px, 1fr)
          );
        `};
      `;
    } else {
      return css`
        display: block;
      `;
    }
  }}
`;

const EmptyWrapper = styled.div`
  padding-top: 40px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  font-size: 14px;
  color: ${textColor.black};
`;

const EmptyIcon = styled.img`
  margin-bottom: 12px;
  width: 120px;
  height: 120px;
  object-fit: contain;
`;

const EmptyActionButton = styled.button`
  ${ResetButtonStyle};
  border: 1px solid ${color.yellow};
  border-radius: 4px;
  padding: 0px 12px;
  height: 36px;
  font-size: 14px;
  color: ${color.yellow};
`;

const ItemWrapper = styled.div`
  position: relative;
  padding-top: 100%;
`;

const LoadingWrapper = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default withTranslation()(ProfilePost);
